import React from 'react';

interface DeviceCategory {
  name: string;
  devices: string[];
  icon: string;
}

const DevicesPage: React.FC = () => {
  const deviceCategories: DeviceCategory[] = [
    {
      name: 'Smart TVs',
      devices: [
        'Samsung Smart TVs (2018 or newer)',
        'LG Smart TVs (webOS 3.0 or newer)',
        'Sony Smart TVs (Android TV)',
        'Vizio SmartCast TVs',
        'Philips Smart TVs (Saphi & Android TV)',
        'Panasonic Smart TVs (2018 or newer)',
        'TCL Smart TVs (Roku & Android TV)'
      ],
      icon: '📺'
    },
    {
      name: 'Streaming Devices',
      devices: [
        'Roku (all devices)',
        'Amazon Fire TV (all devices)',
        'Google Chromecast (all generations)',
        'Apple TV (4th generation and newer)',
        'Nvidia Shield TV',
        'Xiaomi Mi Box'
      ],
      icon: '🎮'
    },
    {
      name: 'Game Consoles',
      devices: [
        'PlayStation 5',
        'PlayStation 4',
        'Xbox Series X & S',
        'Xbox One',
        'Nintendo Switch'
      ],
      icon: '🎮'
    },
    {
      name: 'Mobile Devices',
      devices: [
        'iPhone (iOS 12 or newer)',
        'iPad (iOS 12 or newer)',
        'Android phones (Android 6.0 or newer)',
        'Android tablets (Android 6.0 or newer)',
        'Windows Phone (10 or newer)'
      ],
      icon: '📱'
    },
    {
      name: 'Computers',
      devices: [
        'Windows PC (Windows 8 or newer)',
        'Mac (macOS 10.12 or newer)',
        'Chromebook',
        'Linux (Ubuntu 18.04 or newer)'
      ],
      icon: '💻'
    },
    {
      name: 'Other Devices',
      devices: [
        'Blu-ray players (with Smart features)',
        'Set-top boxes (selected models)',
        'Smart displays (Google Nest Hub, Echo Show)',
        'Smart projectors'
      ],
      icon: '📀'
    }
  ];

  return (
    <main>
      <div className="container">
        <div style={{ marginTop: '120px', marginBottom: '50px' }}>
          <h1 className="section-title">Supported Devices</h1>
          
          <div style={{ marginTop: '30px' }}>
            <p style={{ marginBottom: '30px', lineHeight: '1.6' }}>
              Quick Flick is available on a wide range of devices. You can watch on your TV, computer, mobile devices, and more.
              Below is a list of currently supported devices. We're constantly working to expand this list.
            </p>
            
            <div style={{ 
              marginBottom: '40px', 
              padding: '25px', 
              background: 'var(--secondary-color)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <h2 style={{ marginBottom: '15px', fontSize: '1.3rem' }}>Looking for device-specific help?</h2>
              <p style={{ marginBottom: '20px' }}>
                Visit our Help Center for detailed setup instructions, troubleshooting tips, and device-specific guides.
              </p>
              <a href="/help" className="btn btn-primary">Visit Help Center</a>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px' }}>
              {deviceCategories.map((category, index) => (
                <div 
                  key={index}
                  style={{ 
                    flex: '1 1 350px',
                    background: 'var(--secondary-color)',
                    borderRadius: '8px',
                    padding: '25px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <span style={{ fontSize: '2rem', marginRight: '15px' }}>{category.icon}</span>
                    <h2 style={{ fontSize: '1.3rem' }}>{category.name}</h2>
                  </div>
                  <ul style={{ listStyle: 'disc', marginLeft: '20px' }}>
                    {category.devices.map((device, deviceIndex) => (
                      <li key={deviceIndex} style={{ marginBottom: '8px', color: 'var(--light-gray)' }}>
                        {device}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div style={{ marginTop: '40px' }}>
              <h2 style={{ fontSize: '1.5rem', margin: '30px 0 20px' }}>Device Requirements</h2>
              
              <div style={{ 
                background: 'var(--secondary-color)',
                borderRadius: '8px',
                padding: '25px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginBottom: '15px' }}>Internet Connection</h3>
                <p style={{ marginBottom: '10px' }}>To use Quick Flick, you'll need an internet connection. Here's what we recommend:</p>
                <ul style={{ marginLeft: '20px', listStyle: 'disc' }}>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Standard Definition (SD):</strong> 3 Mbps or faster
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>High Definition (HD):</strong> 5 Mbps or faster
                  </li>
                  <li>
                    <strong>Ultra HD/4K:</strong> 25 Mbps or faster
                  </li>
                </ul>
              </div>
              
              <div style={{ 
                background: 'var(--secondary-color)',
                borderRadius: '8px',
                padding: '25px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginBottom: '15px' }}>Browser Requirements</h3>
                <p style={{ marginBottom: '10px' }}>
                  If you're watching on a web browser, we support the following browsers:
                </p>
                <ul style={{ marginLeft: '20px', listStyle: 'disc' }}>
                  <li style={{ marginBottom: '8px' }}>Google Chrome (latest version)</li>
                  <li style={{ marginBottom: '8px' }}>Firefox (latest version)</li>
                  <li style={{ marginBottom: '8px' }}>Safari (latest version)</li>
                  <li style={{ marginBottom: '8px' }}>Microsoft Edge (latest version)</li>
                  <li>Opera (latest version)</li>
                </ul>
              </div>
            </div>
            
            <div style={{ 
              background: 'var(--secondary-color)',
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              marginTop: '40px'
            }}>
              <h2 style={{ marginBottom: '15px' }}>Don't see your device?</h2>
              <p style={{ marginBottom: '20px', maxWidth: '700px', margin: '0 auto 20px' }}>
                We're constantly adding support for new devices. If your device isn't listed here, 
                check back soon or contact our support team.
              </p>
              <a href="/contact" className="btn btn-primary">Contact Support</a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DevicesPage; 